/**
 * Mode Options for Onboarding
 *
 * Modes are set up to be an easy clustering mechanism for config toggles.
 *
 * The expectation is that a mode will be configured through existing methods,
 * and the config for that mode will then be looked up and configured at the end.
 *
 * @example ExpOnboarding.configure({ mode: 'repReferral' });
 */

import { QUALIFY_DESTINATION } from '../utilities/utilities';

// Type definitions for mode configurations
export type Toggles = {
  applicationConfirmation?: boolean;
  applicationPreview?: boolean;
  avoidScrolling?: boolean;
  categorySelector?: boolean;
  categorySelectorAtTheEnd?: boolean;
  changeRetailLocationFlow?: boolean;
  completeSpinner?: boolean;
  disableAnalytics?: boolean;
  emailRuleSignUp?: boolean;
  exitAfterGroups?: boolean;
  facebook?: boolean;
  facebookContinue?: boolean;
  finalFlow?: boolean;
  groupApplication?: boolean;
  groupCode?: boolean;
  groupCodeOpen?: boolean;
  groupSearch?: boolean;
  guidelinesWithSplash?: boolean;
  handleProgramClosed?: boolean;
  hideProgramElmsInGroupApplication?: boolean;
  ignoreExistingSuggestedGroups?: boolean;
  initialModeToggle?: boolean;
  initialOnly?: boolean;
  isolateNameFields?: boolean;
  locationConfirmation?: boolean;
  locationCreate?: boolean;
  noDefaultLocation?: boolean;
  onboardingComplete?: boolean;
  personalInfo?: boolean;
  personalInfoBeforeGroups?: boolean;
  personalInfoSuccess?: boolean;
  preventClose?: boolean;
  profileStrength?: boolean;
  programApplicationPreamble?: boolean;
  programSuccessV2?: boolean;
  referral?: boolean;
  renewalApplicationFlow?: boolean;
  repReferral?: boolean;
  signupSuccess?: boolean;
  skipCompleteAfterAppSuccess?: boolean;
  splash?: boolean;
  suppressEVGuidelines?: boolean;
};

export type ModeContext = {
  user?: any;
  affiliations?: any[];
  groupCode?: string;
  selectedTeam?: any;
  submittedApplication?: {
    isSubmitted: boolean;
    [key: string]: any;
  };
  setupComplete?: boolean;
  [key: string]: any;
};

export type ModeConfig = {
  inlineConfig?: {
    containerElementId?: string;
    [key: string]: any;
  };
  aiVerificationTiming?: {
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
  };
  emailVerificationTiming?: {
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
  };
  allowAuthenticated?: boolean;
  applicationSuccessMessages?: Record<string, Record<string, string>>;
  autoShow?: boolean;
  basePath?: string;
  blockCustomComplete?: boolean;
  categoriesButtonLabel?: string;
  categoriesSkippable?: boolean;
  close?: (args: ModeArgs) => void;
  complete?: (args: ModeArgs) => void;
  containerElementId?: string;
  customGuidelines?: Record<string, any>;
  destination?: string;
  email?: string;
  ensureTeam?: boolean;
  expectAuthenticated?: boolean;
  firstName?: string;
  groupCode?: string;
  groupSearch?: boolean;
  groupSearchMode?: string;
  groupSearchTerm?: string;
  ifAuthenticated?: IfAuthenticated;
  /** Page key of the landing page the user arrived on; cached at sign-up and sent with analytics */
  initialLandingPageKey?: string;
  initialMode?: 'signIn' | 'signUp';
  isComplete?: (args: ModeArgs) => boolean;
  /** Page key of the landing page an already-signed-in visitor was redirected in from
   * (`?onb_landing=healthcare`). Curates the Top Teams list and its headline for that visit
   * only — never cached, and deliberately not fed to the search narrowing */
  landing?: string;
  /** Team class IDs associated with the landing page; cached at sign-up and sent with analytics */
  landingTeamClassIds?: number[];
  lastName?: string;
  map?: {
    defaultLatitude: number;
    defaultLongitude: number;
    defaultZoom: number;
    scriptUrl: string;
    searchPageSize: number;
    searchRadius: number;
  };
  maxCategories?: number;
  memberId?: string;
  mode?: string;
  previewConfig?: {
    questions?: any[];
    [key: string]: any;
  };
  programKey?: string;
  qualifyDestination?: string;
  referer?: string;
  referredOrgId?: number;
  sanitizeDestination?: (destination: string, args: ModeArgs) => string;
  searchTeams?: number[];
  selectedCategories?: number[];
  setupDestination?: string;
  signInDestination?: string;
  signUpDestination?: string;
  staleConfig?: boolean;
  suggestedGroups?: number[];
  supportEmail?: string;
  supportPhone?: string;
  teamClass?: number | number[];
  teamId?: number;
  teamKey?: string;
  teamTargetId?: string;
  theme?: string;
  toggles?: Toggles;
  uris?: {
    cdn?: string;
    cookiePolicy?: string;
    forgotPassword?: string;
    passwordReset?: string;
    privacyPolicy?: string;
    userAgreement?: string;
  };
  [key: string]: any;
};

export type ModeArgs = {
  config: ModeConfig;
  context: ModeContext;
};

export type IfAuthenticated = {
  autoShow?: boolean;
  toggles?: Toggles;
};

export type ModeConfiguration = {
  allowAuthenticated?: boolean;
  autoShow?: boolean;
  blockCustomComplete?: boolean;
  categoriesButtonLabel?: string;
  categoriesSkippable?: boolean;
  complete?: (args: ModeArgs) => void;
  ensureTeam?: boolean;
  expectAuthenticated?: boolean;
  groupSearch?: boolean;
  ifAuthenticated?: IfAuthenticated;
  isComplete?: (args: ModeArgs) => boolean;
  name?: string;
  parse?: (config: ModeConfig) => void;
  qualifyDestination?: string;
  referralTeamSelector?: boolean;
  sanitizeDestination?: (destination: string, args: ModeArgs) => string;
  signInDestination?: string;
  signUpDestination?: string;
  standaloneCategoriesPage?: boolean;
  standaloneCodeFlow?: boolean;
  standaloneGroupsFlow?: boolean;
  theme?: string;
  toggles?: Toggles;
};

export type ModeFactory = () => ModeConfiguration;

export type Mode = ModeConfiguration | ModeFactory;

const Modes: Record<string, Mode> = {
  // Standalone application preview mode that only shows the application preview page
  applicationPreview: {
    allowAuthenticated: true,
    toggles: {
      applicationPreview: true,
      avoidScrolling: true,
      disableAnalytics: true,
      profileStrength: false,
    },
  },

  // Standalone Category Selector Mode
  categorySelector: {
    categoriesButtonLabel: 'onb.button.save',
    categoriesSkippable: false,
    expectAuthenticated: true,
    // TODO: implement isComplete method that checks that categories have been selected or changed
    standaloneCategoriesPage: true,
  },

  // Allows the user to change the location of their retail affiliation
  changeRetailLocation: {
    expectAuthenticated: true,
    standaloneGroupsFlow: true,
    toggles: {
      changeRetailLocationFlow: true,
      profileStrength: false,
    },
  },

  // Continue onboarding mode
  continue: {
    expectAuthenticated: true,
  },

  // Embedded sign-up mode that only shows the initial page. Expected to continue with the 'continueOnboarding' mode.
  embeddedSignUp: {
    allowAuthenticated: true, // This lets the form show even for authenticated users
    complete: ({ config, context }) => {
      const { containerElementId, ...inline } = config.inlineConfig || {};

      // Continue onboarding in the non-embedded mode, forwarding the groupCode if one was provided.
      window.ExpOnboarding.show({
        ...inline,
        destination: config.destination || config.signUpDestination,
        expectAuthenticated: true,
        groupCode: context.groupCode,
        mode: 'continue',
        staleConfig: true,
      });
    },
    toggles: {
      initialModeToggle: false,
      initialOnly: true,
    },
  },
  // Facebook Connect Continuation Mode
  facebookConnect: (): ModeConfiguration => {
    const options: ModeConfiguration = {
      ifAuthenticated: {
        autoShow: true,
      },
      toggles: {
        facebookContinue: false,
      },
    };

    // Only apply the mode if the connectionStatus=CREATED exists as a query parameter (put there by sign-on-web).
    const params = new URLSearchParams(window.location.search);
    if (params.get('connectionStatus') === 'CREATED') {
      options.expectAuthenticated = true;
      options.toggles.facebookContinue = true;
      options.toggles.personalInfo = false; // Facebook Info page handles personal info
    }

    return options;
  },

  // Standalone Manage Groups Mode
  manageGroups: {
    expectAuthenticated: true,
    // Complete if the user submits an application as part of the flow
    isComplete: ({ context }) => context.submittedApplication?.isSubmitted,
    standaloneGroupsFlow: true,
    name: 'manageGroups',
    toggles: {
      changeRetailLocationFlow: false,
      groupSearch: true,
      profileStrength: false,
    },
  },

  // Standalone Post Affiliation Mode
  postAffiliation: {
    expectAuthenticated: true,
    name: 'postAffiliation',
  },

  // Personalization of users interest
  interests: {
    allowAuthenticated: true,
    expectAuthenticated: true,
    name: 'interests',
    theme: 'v3',
  },
  // Standalone Invite Code Mode
  inviteCode: {
    expectAuthenticated: true,
    // Complete if the user submits an application as part of the flow
    isComplete: ({ context }) => context.submittedApplication?.isSubmitted,
    name: 'inviteCode',
    standaloneCodeFlow: true,
    toggles: {
      profileStrength: false,
    },
  },

  // Standard Onboarding Mode
  onboarding: {
    name: 'onboarding',
  },

  // Takeover Onboarding Mode
  takeover: {
    name: 'takeover',
    signInDestination: '/',
    signUpDestination: '/user/setup',
    theme: 'v3',
    toggles: {
      categorySelector: false,
      isolateNameFields: true,
    },
  },

  // Takeover Onboarding Mode for vets
  vets: {
    name: 'vets',
    signInDestination: '/',
    signUpDestination: '/user/setup',
    theme: 'vets',
    toggles: {
      categorySelector: false,
      initialModeToggle: false,
      isolateNameFields: true,
    },
  },

  // Takeover Setup Mode
  setup: {
    allowAuthenticated: true,
    expectAuthenticated: true,
    isComplete: ({ context }) => context.setupComplete,
    name: 'setup',
    qualifyDestination: QUALIFY_DESTINATION,
    theme: 'v3',
  },

  qualify: {
    allowAuthenticated: true,
    expectAuthenticated: true,
    name: 'qualify',
    theme: 'v3',
  },

  manageTeams: {
    allowAuthenticated: true,
    expectAuthenticated: true,
    name: 'manageTeams',
    theme: 'v3',
  },
  // Partner Team Onboarding Mode
  partner: {
    ensureTeam: true,
    toggles: {
      groupSearch: true,
    },
  },

  // Program Onboarding Mode
  program: {
    allowAuthenticated: true,
    ensureTeam: true,
    signUpDestination: 'profile',
    theme: 'v2',
    toggles: {
      applicationConfirmation: true,
      programApplicationPreamble: true,
      categorySelector: false,
      emailRuleSignUp: false,
      groupSearch: false,
      guidelinesWithSplash: true,
      handleProgramClosed: true,
      hideProgramElmsInGroupApplication: true,
      initialModeToggle: true,
      noDefaultLocation: true,
      personalInfo: false,
      programSuccessV2: true,
      signupSuccess: true,
      skipCompleteAfterAppSuccess: true,
      splash: true,
    },
  },

  // Standard Referral Mode
  referral: {
    allowAuthenticated: true,
    autoShow: true,
    ensureTeam: true,
    name: 'referral',
    ifAuthenticated: {
      toggles: {
        initialOnly: true,
      },
    },
    toggles: {
      facebook: false,
      referral: true,
    },
  },

  // Used when a user is selecting a team for a referral invite
  referralTeamSelector: {
    expectAuthenticated: true,
    isComplete: ({ context }) => !!context.selectedTeam,
    parse: (config) => {
      if (config.suggestedGroups?.length) {
        // Turn group search off if suggested groups were specified
        return { ...config, groupSearch: false };
      }
      return config;
    },
    referralTeamSelector: true,
    standaloneGroupsFlow: true,
    toggles: {
      groupApplication: false,
      ignoreExistingSuggestedGroups: true,
      profileStrength: false,
    },
  },

  // Renewal Application Mode
  renewalApplication: {
    expectAuthenticated: true,
    // Complete if the user submits an application as part of the flow
    isComplete: ({ context }) => context.submittedApplication?.isSubmitted,
    standaloneGroupsFlow: true,
    toggles: {
      changeRetailLocationFlow: false,
      groupSearch: false,
      profileStrength: false,
      renewalApplicationFlow: true,
    },
  },

  // Custom Rep Referral Mode
  repReferral: {
    allowAuthenticated: true,
    autoShow: true,
    blockCustomComplete: true,
    ensureTeam: true,
    name: 'repReferral',
    ifAuthenticated: {
      // Turn the personal info flow off if the user is already authenticated
      toggles: {
        finalFlow: false,
        groupSearch: false,
        onboardingComplete: false,
        profileStrength: false,
      },
    },
    sanitizeDestination: (destination, { config, context }) => {
      // Sanitize the destination so the repReferral mode doesn't hang around
      if (!destination.includes('?')) {
        return destination;
      }

      const params = new URLSearchParams(destination.substring(destination.indexOf('?')));
      params.delete('onb_mode');
      params.delete('repReferred');

      const affiliation = context.affiliations?.find((a) => {
        if (config.teamKey && config.teamKey === a.teamKey) return true;
        if (config.teamId && config.teamId === a.teamId) return true;
        return config.suggestedGroups?.includes(a.teamId);
      });
      if (affiliation) {
        params.set('repReferred', 'true');
      }

      return `${destination.substring(0, destination.indexOf('?'))}?${params.toString()}`;
    },
    toggles: {
      categorySelector: false,
      facebook: false,
      groupCode: false,
      groupSearch: true,
      locationConfirmation: true,
      referral: true,
      repReferral: true,
    },
  },
};

export default Modes;
